import { keepPreviousData, QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { ParamsWithPagination, PaginatedResponse } from '@/types/api';
import { normalizeIds, requestInChunks } from '@/utils/ids';

import { TFeaturedPeopleType } from '../types';
import { FeaturedPeopleFilters } from '../types';

import { featuredPeopleQueryKeys } from './query-keys';

/**
 * RISK: `limit` is required. Without one the endpoint answers its first 100 people, and a
 * caller reading "the catalogue" that way silently drops every older person.
 */
type FeaturedPeopleRequestParams = ParamsWithPagination<FeaturedPeopleFilters> & {
  limit: number;
};

export async function getFeaturedPeople(
  params: FeaturedPeopleRequestParams
): Promise<PaginatedResponse<TFeaturedPeopleType>> {
  return mediaManagerClient.get('api/v1/peoples', {
    params: {
      limit: params.limit,
      offset: params.offset,
      id__in: params.idIn?.join(','),
      name: params.name,
      order_by: params.order_by,
      search: params.search,
      state__in: params.stateIn?.join(','),
      updated_by__in: params.updatedByIn?.join(','),
      segment_start_datetime__gte: params.segmentStartDatetimeGte,
      segment_end_datetime__lte: params.segmentEndDatetimeLte,
      clip_start_datetime__gte: params.clipStartDatetimeGte,
      clip_end_datetime__lte: params.clipEndDatetimeLte,
    },
  });
}

type QueryFnType = typeof getFeaturedPeople;

interface UseFeaturedPeopleParams {
  config?: QueryConfig<QueryFnType>;
  params: FeaturedPeopleRequestParams;
}

export function useFeaturedPeople({ config, params }: UseFeaturedPeopleParams) {
  return useQuery({
    ...config,
    queryKey: featuredPeopleQueryKeys.list(params),
    queryFn: () => getFeaturedPeople(params),
  });
}

export const FEATURED_PEOPLE_PAGE_SIZE = 25;

/**
 * `name` is not unique, and the endpoint pages by slicing `offset:offset+limit`. Without a
 * tiebreaker the order is not total, so a person can land on two consecutive pages or on none.
 */
export const FEATURED_PEOPLE_ORDER_BY = 'name asc,id asc';

export type FeaturedPeoplePageParams = Pick<
  ParamsWithPagination<FeaturedPeopleFilters>,
  | 'search'
  | 'segmentStartDatetimeGte'
  | 'segmentEndDatetimeLte'
  | 'clipStartDatetimeGte'
  | 'clipEndDatetimeLte'
>;

/**
 * The people catalogue, one page at a time, ordered by name. Changing `search` changes the
 * query key, so react-query restarts paging from offset 0 on its own.
 */
export function useInfiniteFeaturedPeople(
  params: FeaturedPeoplePageParams = {},
  config: { staleTime?: number; enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    ...config,
    queryKey: featuredPeopleQueryKeys.infiniteList(params),
    queryFn: ({ pageParam = 0 }) =>
      getFeaturedPeople({
        ...params,
        offset: pageParam,
        limit: FEATURED_PEOPLE_PAGE_SIZE,
        order_by: FEATURED_PEOPLE_ORDER_BY,
      }),
    initialPageParam: 0,
    // Without it the list empties for a round-trip on every settled keystroke.
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.data.length === FEATURED_PEOPLE_PAGE_SIZE
        ? allPages.reduce((total, page) => total + page.data.length, 0)
        : undefined,
  });
}

/** Stores a person the client already holds, e.g. one just created, as the answer for their id. */
export function seedSelectedFeaturedPeople(queryClient: QueryClient, person: TFeaturedPeopleType) {
  queryClient.setQueryData(featuredPeopleQueryKeys.selected([person.id]), {
    data: [person],
    total: 1,
  });
}

/**
 * Resolves people ids to people, whichever page of the catalogue they belong to.
 *
 * `keepPrevious` keeps the previous answer while a changed set loads, for views that list
 * rows. It is opt-in: a reader that treats any data as "resolved" would otherwise judge the
 * new ids by the old answer. `initialData` is the answer when the caller already holds every
 * person, e.g. from a loaded page.
 */
export function useSelectedFeaturedPeople(
  ids: string[],
  {
    keepPrevious = false,
    initialData,
  }: { keepPrevious?: boolean; initialData?: PaginatedResponse<TFeaturedPeopleType> } = {}
) {
  return useQuery({
    enabled: ids.length > 0,
    initialData,
    queryKey: featuredPeopleQueryKeys.selected(ids),
    queryFn: async () => {
      const pages = await requestInChunks(normalizeIds(ids), idIn =>
        getFeaturedPeople({ idIn, limit: idIn.length })
      );

      return {
        ...pages[0],
        data: pages.flatMap(page => page.data),
        total: pages.reduce((n, page) => n + page.total, 0),
      };
    },
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
}
